/**
 * 认证相关工具函数
 * 
 * @description 提供认证相关的工具函数，包括令牌管理、状态检查等
 * @author Cross-Border ERP Team
 * @version 1.0.0
 */

import type { CurrentUser } from '@/services/auth.d';

// ============= 令牌管理 =============

/**
 * 存储键名常量
 */
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'token',
  REFRESH_TOKEN: 'refresh_token',
  USER_INFO: 'user_info',
  LANGUAGE: 'language',
  THEME: 'theme',
} as const;

/**
 * 检查用户是否已登录
 * 
 * @description 检查本地存储中是否存在有效的访问令牌
 * @returns boolean
 * 
 * @example
 * ```typescript
 * if (isLoggedIn()) {
 *   console.log('用户已登录');
 * } else {
 *   console.log('用户未登录，需要跳转到登录页');
 * }
 * ```
 */
export function isLoggedIn(): boolean {
  const token = getAuthToken();
  return !!token && !isTokenExpired(token);
}

/**
 * 获取当前用户的访问令牌
 * 
 * @description 从本地存储获取访问令牌
 * @returns string | null
 * 
 * @example
 * ```typescript
 * const token = getAuthToken();
 * if (token) {
 *   // 使用令牌进行API请求
 * }
 * ```
 */
export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  } catch (error) {
    console.warn('无法获取访问令牌:', error);
    return null;
  }
}

/**
 * 设置访问令牌
 * 
 * @description 将访问令牌存储到本地存储
 * @param token 访问令牌
 * 
 * @example
 * ```typescript
 * setAuthToken('your-jwt-token');
 * ```
 */
export function setAuthToken(token: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  } catch (error) {
    console.error('无法设置访问令牌:', error);
  }
}

/**
 * 获取刷新令牌
 * 
 * @description 从本地存储获取刷新令牌
 * @returns string | null
 */
export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  } catch (error) {
    console.warn('无法获取刷新令牌:', error);
    return null;
  }
}

/**
 * 设置刷新令牌
 * 
 * @description 将刷新令牌存储到本地存储
 * @param token 刷新令牌
 */
export function setRefreshToken(token: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  } catch (error) {
    console.error('无法设置刷新令牌:', error);
  }
}

/**
 * 清除访问令牌
 * 
 * @description 从本地存储移除访问令牌
 * 
 * @example
 * ```typescript
 * clearAuthToken(); // 清除访问令牌
 * ```
 */
export function clearAuthToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  } catch (error) {
    console.error('无法清除访问令牌:', error);
  }
}

/**
 * 清除所有认证相关数据
 * 
 * @description 清除所有本地存储的认证相关数据
 * 
 * @example
 * ```typescript
 * clearAllAuthData(); // 用户登出时调用
 * ```
 */
export function clearAllAuthData(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_INFO);
  } catch (error) {
    console.error('无法清除认证数据:', error);
  }
}

// ============= 令牌解析与验证 =============

/**
 * 解析JWT令牌
 * 
 * @description 解析JWT令牌的payload部分
 * @param token JWT令牌
 * @returns any | null
 * 
 * @example
 * ```typescript
 * const payload = parseJwtToken(token);
 * if (payload) {
 *   console.log('用户ID:', payload.sub);
 *   console.log('过期时间:', payload.exp);
 * }
 * ```
 */
export function parseJwtToken(token: string): any | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.warn('无法解析JWT令牌:', error);
    return null;
  }
}

/**
 * 检查令牌是否过期
 * 
 * @description 检查JWT令牌是否已过期
 * @param token JWT令牌
 * @returns boolean
 * 
 * @example
 * ```typescript
 * const token = getAuthToken();
 * if (token && isTokenExpired(token)) {
 *   console.log('令牌已过期，需要刷新');
 * }
 * ```
 */
export function isTokenExpired(token: string): boolean {
  const payload = parseJwtToken(token);
  if (!payload || !payload.exp) {
    return true;
  }
  
  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp < currentTime;
}

/**
 * 获取令牌剩余有效时间（秒）
 * 
 * @description 计算JWT令牌的剩余有效时间
 * @param token JWT令牌
 * @returns number 剩余秒数，-1表示已过期或无效
 */
export function getTokenRemainingTime(token: string): number {
  const payload = parseJwtToken(token);
  if (!payload || !payload.exp) {
    return -1;
  }
  
  const currentTime = Math.floor(Date.now() / 1000);
  const remainingTime = payload.exp - currentTime;
  return Math.max(0, remainingTime);
}

// ============= 用户信息管理 =============

/**
 * 获取缓存的用户信息
 * 
 * @description 从本地存储获取用户信息
 * @returns CurrentUser | null
 * 
 * @example
 * ```typescript
 * const user = getCachedUserInfo();
 * if (user) {
 *   console.log('当前用户:', user.name);
 * }
 * ```
 */
export function getCachedUserInfo(): CurrentUser | null {
  try {
    const userInfoStr = localStorage.getItem(STORAGE_KEYS.USER_INFO);
    return userInfoStr ? JSON.parse(userInfoStr) : null;
  } catch (error) {
    console.warn('无法获取用户信息:', error);
    return null;
  }
}

/**
 * 缓存用户信息
 * 
 * @description 将用户信息存储到本地存储
 * @param userInfo 用户信息
 * 
 * @example
 * ```typescript
 * setCachedUserInfo(userInfo);
 * ```
 */
export function setCachedUserInfo(userInfo: CurrentUser): void {
  try {
    localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));
  } catch (error) {
    console.error('无法缓存用户信息:', error);
  }
}

/**
 * 清除缓存的用户信息
 * 
 * @description 从本地存储移除用户信息
 */
export function clearCachedUserInfo(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.USER_INFO);
  } catch (error) {
    console.error('无法清除用户信息:', error);
  }
}

// ============= 用户偏好设置 =============

/**
 * 获取用户语言偏好
 * 
 * @description 获取用户设置的语言偏好
 * @returns string 语言代码，默认为 'zh-CN'
 */
export function getUserLanguage(): string {
  try {
    return localStorage.getItem(STORAGE_KEYS.LANGUAGE) || 'zh-CN';
  } catch (error) {
    console.warn('无法获取语言设置:', error);
    return 'zh-CN';
  }
}

/**
 * 设置用户语言偏好
 * 
 * @description 设置用户的语言偏好
 * @param language 语言代码
 */
export function setUserLanguage(language: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, language);
  } catch (error) {
    console.error('无法设置语言:', error);
  }
}

/**
 * 获取用户主题偏好
 * 
 * @description 获取用户设置的主题偏好
 * @returns string 主题名称，默认为 'light'
 */
export function getUserTheme(): string {
  try {
    return localStorage.getItem(STORAGE_KEYS.THEME) || 'light';
  } catch (error) {
    console.warn('无法获取主题设置:', error);
    return 'light';
  }
}

/**
 * 设置用户主题偏好
 * 
 * @description 设置用户的主题偏好
 * @param theme 主题名称
 */
export function setUserTheme(theme: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.THEME, theme);
  } catch (error) {
    console.error('无法设置主题:', error);
  }
}

// ============= 权限检查 =============

/**
 * 检查用户是否具有指定权限
 * 
 * @description 检查当前用户是否具有指定的权限
 * @param permission 权限名称
 * @returns boolean
 * 
 * @example
 * ```typescript
 * if (hasPermission('admin.users.create')) {
 *   // 显示创建用户按钮
 * }
 * ```
 */
export function hasPermission(permission: string): boolean {
  const userInfo = getCachedUserInfo();
  if (!userInfo || !userInfo.roles) {
    return false;
  }
  
  // 这里可以根据实际的权限系统实现更复杂的逻辑
  return userInfo.roles.includes('admin') || userInfo.roles.includes(permission);
}

/**
 * 检查用户是否具有任一指定权限
 * 
 * @description 检查当前用户是否具有任一指定的权限
 * @param permissions 权限名称数组
 * @returns boolean
 */
export function hasAnyPermission(permissions: string[]): boolean {
  return permissions.some(permission => hasPermission(permission));
}

/**
 * 检查用户是否具有所有指定权限
 * 
 * @description 检查当前用户是否具有所有指定的权限
 * @param permissions 权限名称数组
 * @returns boolean
 */
export function hasAllPermissions(permissions: string[]): boolean {
  return permissions.every(permission => hasPermission(permission));
}

// ============= 会话管理 =============

/**
 * 检查会话是否即将过期
 * 
 * @description 检查当前会话是否在指定时间内即将过期
 * @param thresholdMinutes 阈值（分钟），默认为5分钟
 * @returns boolean
 */
export function isSessionExpiringSoon(thresholdMinutes: number = 5): boolean {
  const token = getAuthToken();
  if (!token) {
    return false;
  }
  
  const remainingTime = getTokenRemainingTime(token);
  const thresholdSeconds = thresholdMinutes * 60;
  
  return remainingTime > 0 && remainingTime <= thresholdSeconds;
}

/**
 * 格式化令牌过期时间
 * 
 * @description 将令牌过期时间格式化为人类可读的字符串
 * @param token JWT令牌
 * @returns string 格式化的过期时间
 */
export function formatTokenExpiration(token: string): string {
  const payload = parseJwtToken(token);
  if (!payload || !payload.exp) {
    return '无效令牌';
  }
  
  const expirationDate = new Date(payload.exp * 1000);
  return expirationDate.toLocaleString();
}

